import { 
  StoreDisplayGroupPlanogramApply, 
  ApiResponse, 
  PaginatedResponse, 
  StoreDisplayGroupPlanogramApplyFilters, 
  PaginationParams,
  CreateStoreDisplayGroupPlanogramApplyRequest,
  UpdateStoreDisplayGroupPlanogramApplyRequest,
  AuditStoreDisplayGroupPlanogramApplyRequest,
} from '../types/storeDisplayGroupPlanogramApply';
import { 
  mockStoreDisplayGroupPlanogramApplies, 
  getApplyById,
  getStoreCodeOptions,
  getPlanogramCodeOptions,
  getDisplayGroupCodeOptions,
  createMockApply,
  updateMockApply,
  deleteMockApply
} from '../mock/storeDisplayGroupPlanogramApplyData';
import { validateFilters } from '../utils/validation';
import { createSuccessResponse, createErrorResponse, createPaginatedResponse } from '../utils/responseTransformer';
import { createErrorInfo, logError } from '../utils/errorHandler';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import { warehouseService } from './warehouseService';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

// In-memory storage for demo purposes
let storeDisplayGroupPlanogramApplies = [...mockStoreDisplayGroupPlanogramApplies];

/**
 * Service class for Store Display Group Planogram Apply operations
 * Supports both Mock data and real API calls
 */
export class StoreDisplayGroupPlanogramApplyService {
  private static instance: StoreDisplayGroupPlanogramApplyService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;

  /**
   * Get singleton instance
   */
  public static getInstance(): StoreDisplayGroupPlanogramApplyService {
    if (!StoreDisplayGroupPlanogramApplyService.instance) {
      StoreDisplayGroupPlanogramApplyService.instance = new StoreDisplayGroupPlanogramApplyService();
    }
    return StoreDisplayGroupPlanogramApplyService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    StoreDisplayGroupPlanogramApplyService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return StoreDisplayGroupPlanogramApplyService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get paginated list of applies with filters
   * @param filters Filter criteria
   * @param pagination Pagination parameters
   * @returns Promise with paginated apply list
   */
  public async getApplies(
    filters: StoreDisplayGroupPlanogramApplyFilters = {},
    pagination: PaginationParams = { page: 1, pageSize: 20 }
  ): Promise<ApiResponse<PaginatedResponse<StoreDisplayGroupPlanogramApply>>> {
    try {
      // Validate input parameters
      if (!validateFilters(filters)) {
        return createErrorResponse('请求参数有误，请检查后重试', 'INVALID_PARAMS');
      }

      if (StoreDisplayGroupPlanogramApplyService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(300);

        let filteredApplies = [...storeDisplayGroupPlanogramApplies].filter(apply => apply.deleted === 0);
        filteredApplies = filteredApplies.filter(apply => apply.source === filters.source);
        // Apply filters
        if (filters.applyNo) {
          filteredApplies = filteredApplies.filter(apply => 
            apply.applyNo.toLowerCase().includes(filters.applyNo!.toLowerCase())
          );
        }

        if (filters.storeCode) {
          filteredApplies = filteredApplies.filter(apply => apply.storeCode === filters.storeCode);
        }

        if (filters.storeName) {
          filteredApplies = filteredApplies.filter(apply => 
            apply.storeName.toLowerCase().includes(filters.storeName!.toLowerCase())
          );
        }

        if (filters.displayGroupCode) {
          filteredApplies = filteredApplies.filter(apply => apply.displayGroupCode === filters.displayGroupCode);
        }

        if (filters.teamType) {
          filteredApplies = filteredApplies.filter(apply => apply.teamType === filters.teamType);
        }

        if (filters.applyStatus) {
          filteredApplies = filteredApplies.filter(apply => apply.applyStatus === filters.applyStatus);
        }

        if (filters.applyPerson) {
          filteredApplies = filteredApplies.filter(apply => 
            apply.applyPerson.toLowerCase().includes(filters.applyPerson!.toLowerCase()) ||
            apply.applyPersonName.toLowerCase().includes(filters.applyPerson!.toLowerCase())
          );
        }

        if (filters.startDate) {
          filteredApplies = filteredApplies.filter(apply => apply.applyTime >= filters.startDate!);
        }

        if (filters.endDate) {
          filteredApplies = filteredApplies.filter(apply => apply.applyTime <= filters.endDate!);
        }

        // Apply search filter
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          filteredApplies = filteredApplies.filter(apply => 
            apply.applyNo.toLowerCase().includes(searchTerm) ||
            apply.storeName.toLowerCase().includes(searchTerm) ||
            apply.displayGroupName.toLowerCase().includes(searchTerm) ||
            apply.applyPersonName.toLowerCase().includes(searchTerm) ||
            apply.applyReason.toLowerCase().includes(searchTerm)
          );
        }

        // Apply sorting
        if (pagination.sortBy) {
          filteredApplies.sort((a, b) => {
            let aValue: any = a[pagination.sortBy as keyof StoreDisplayGroupPlanogramApply];
            let bValue: any = b[pagination.sortBy as keyof StoreDisplayGroupPlanogramApply];
            
            // Handle date sorting
            if (pagination.sortBy === 'applyTime' || pagination.sortBy === 'auditTime' || 
                pagination.sortBy === 'gmtCreate' || pagination.sortBy === 'gmtModified') {
              aValue = new Date(aValue || 0).getTime();
              bValue = new Date(bValue || 0).getTime();
            }
            
            if (pagination.sortOrder === 'desc') {
              return aValue > bValue ? -1 : 1;
            }
            return aValue > bValue ? 1 : -1;
          });
        } else {
          // Default sort by gmtCreate desc
          filteredApplies.sort((a, b) => new Date(b.gmtCreate).getTime() - new Date(a.gmtCreate).getTime());
        }

        // Apply pagination
        const total = filteredApplies.length;
        const startIndex = (pagination.page - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const tableData = filteredApplies.slice(startIndex, endIndex);

        return createPaginatedResponse(tableData, pagination.page, pagination.pageSize, total);
      } else {
        // Call real API
        const response = await httpClient.post(
          API_ENDPOINTS.STORE_DISPLAY_GROUP_PLANOGRAM_APPLY.PAGE,
          {
            ...filters,
            applyTimeStart: filters.startDate,
            applyTimeEnd: filters.endDate,
            currentPage: pagination.page,
            pageSize: pagination.pageSize,
            sortBy: pagination.sortBy,
            sortOrder: pagination.sortOrder
          }
        );
        return response.data;
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取门店陈列分组计划图申请列表失败');
      logError(errorInfo, 'getApplies');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

  /**
   * Get apply by ID
   * @param id Apply ID
   * @returns Promise with apply details
   */
  public async getApplyById(id: number): Promise<ApiResponse<StoreDisplayGroupPlanogramApply>> {
    try {
      if (StoreDisplayGroupPlanogramApplyService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(200);

        const apply = getApplyById(id);
        
        if (!apply || apply.deleted === 1) {
          return createErrorResponse('申请不存在', 'NOT_FOUND');
        }

        return createSuccessResponse(apply);
      } else {
        // Call real API
        const response = await httpClient.get(`${API_ENDPOINTS.STORE_DISPLAY_GROUP_PLANOGRAM_APPLY.GET_BY_ID}/${id}`);
        return response.data;
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取申请详情失败');
      logError(errorInfo, 'getApplyById');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

  /**
   * Audit apply
   * @param request Audit apply request
   * @returns Promise with audited apply
   */
  public async auditApply(request: AuditStoreDisplayGroupPlanogramApplyRequest): Promise<ApiResponse<StoreDisplayGroupPlanogramApply>> {
    try {
      if (StoreDisplayGroupPlanogramApplyService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(400);

        const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
        const updatedApply = updateMockApply(request.id, {
          applyStatus: request.applyStatus,
          auditRemark: request.auditRemark,
          auditPerson: 'current_auditor',
          auditPersonName: '当前审批人',
          auditTime: now
        });
        
        if (!updatedApply) {
          return createErrorResponse('申请不存在', 'NOT_FOUND');
        }

        return createSuccessResponse(updatedApply);
      } else {
        // Call real API
        let req = {...request};
        req.auditStatus = request.applyStatus;
        const response = await httpClient.post(
          API_ENDPOINTS.STORE_DISPLAY_GROUP_PLANOGRAM_APPLY.AUDIT,
          req
        );
        return response.data;
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '审批申请失败');
      logError(errorInfo, 'auditApply');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

  /**
   * Get store code options for dropdown
   * @returns Promise with store code options
   */
  public async getStoreCodeOptions(): Promise<ApiResponse<Array<{ code: string; name: string }>>> {
    try {
      if (StoreDisplayGroupPlanogramApplyService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(100);
        return createSuccessResponse(getStoreCodeOptions());
      } else {
        // Call real API
        // 使用warehouseService.listWarehouse方法获取所有门店，然后过滤出属于当前门店组的门店
        return warehouseService.listWarehouse({});
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取门店选项失败');
      logError(errorInfo, 'getStoreCodeOptions');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

  /**
   * Get planogram code options for dropdown
   * @returns Promise with planogram code options
   */
  public async getPlanogramCodeOptions(): Promise<ApiResponse<Array<{ code: string; name: string }>>> {
    try {
      if (StoreDisplayGroupPlanogramApplyService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(100);
        return createSuccessResponse(getPlanogramCodeOptions());
      } else {
        // Call real API
        const response = await httpClient.get(API_ENDPOINTS.STORE_DISPLAY_GROUP_PLANOGRAM_APPLY.PLANOGRAM_OPTIONS);
        return response.data;
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取计划图选项失败');
      logError(errorInfo, 'getPlanogramCodeOptions');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }

  /**
   * Get display group code options for dropdown
   * @returns Promise with display group code options
   */
  public async getDisplayGroupCodeOptions(): Promise<ApiResponse<Array<{ code: string; name: string }>>> {
    try {
      if (StoreDisplayGroupPlanogramApplyService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(100);
        return createSuccessResponse(getDisplayGroupCodeOptions());
      } else {
        // Call real API
        const response = await httpClient.get(API_ENDPOINTS.STORE_DISPLAY_GROUP_PLANOGRAM_APPLY.DISPLAY_GROUP_OPTIONS);
        return response.data;
      }
    } catch (error) {
      const errorInfo = createErrorInfo(error, '获取陈列分组选项失败');
      logError(errorInfo, 'getDisplayGroupCodeOptions');
      return createErrorResponse(errorInfo.message, errorInfo.code);
    }
  }
}

// Export singleton instance
export default StoreDisplayGroupPlanogramApplyService.getInstance();